import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Tabs, Tab, Box, Typography, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Checkbox,
  CircularProgress, Chip, IconButton
} from '@mui/material';
import { Sync as SyncIcon, Close as CloseIcon } from '@mui/icons-material';
import api from '../../api/axios';
import { useAuth } from '../../context/AuthContext';
import { parseUtc } from '../../utils/time';


function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

const SyncModal = ({ open, onClose }) => {
  const { user } = useAuth();
  const [tab, setTab] = useState(0);
  
  const [sheetData, setSheetData] = useState([]);
  const [loadingSheets, setLoadingSheets] = useState(false);
  const [selectedSheets, setSelectedSheets] = useState([]);
  const [importingSheets, setImportingSheets] = useState(false);

  const [calendarData, setCalendarData] = useState([]);
  const [loadingCalendar, setLoadingCalendar] = useState(false);
  const [selectedCalendar, setSelectedCalendar] = useState([]);
  const [updatingCalendar, setUpdatingCalendar] = useState(false);

  useEffect(() => {
    if (open) {
      if (tab === 0) fetchSheets();
      if (tab === 1) fetchCalendar();
    }
  }, [open, tab]);

  const fetchSheets = async () => {
    if (!user?.organization_id) return;
    setLoadingSheets(true);
    try {
      const res = await api.get(`/sync/sheets?organization_id=${user.organization_id}&limit=100`);
      setSheetData(res.data.entries || []);
      // Auto-select ones that aren't imported yet
      setSelectedSheets((res.data.entries || []).filter(e => !e.is_imported).map(e => e.entry_id));
    } catch (err) {
      console.error(err);
    }
    setLoadingSheets(false);
  };

  const fetchCalendar = async () => {
    if (!user?.organization_id) return;
    setLoadingCalendar(true);
    try {
      const res = await api.get(`/sync/calendar?organization_id=${user.organization_id}&days_back=7`);
      setCalendarData(res.data.events || []);
    } catch (err) {
      console.error(err);
    }
    setLoadingCalendar(false);
  };

  const handleImportSheets = async () => {
    if (!user?.organization_id || selectedSheets.length === 0) return;
    setImportingSheets(true);
    try {
      await api.post('/sync/sheets/import', {
        organization_id: user.organization_id,
        entry_ids: selectedSheets
      });
      await fetchSheets(); // Refresh
    } catch (err) {
      console.error(err);
      alert('Error importing sheet rows');
    }
    setImportingSheets(false);
  };

  const handleUpdateCalendarStatus = async (status) => {
    if (!user?.organization_id || selectedCalendar.length === 0) return;
    setUpdatingCalendar(true);
    try {
      await api.post('/sync/calendar/update-status', {
        organization_id: user.organization_id,
        event_ids: selectedCalendar,
        status: status
      });
      await fetchCalendar(); // Refresh
      setSelectedCalendar([]);
    } catch (err) {
      console.error(err);
      alert('Error updating status');
    }
    setUpdatingCalendar(false);
  };

  const toggleSheetSelect = (id) => {
    setSelectedSheets(prev => 
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const toggleCalendarSelect = (id) => {
    setSelectedCalendar(prev => 
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SyncIcon color="primary" /> Data Sync Center
        </Typography>
        <IconButton onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>
      
      <DialogContent dividers>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tab} onChange={(e, v) => setTab(v)}>
            <Tab label="1. Sheet Intake (New Jobs)" />
            <Tab label="2. Calendar Sync (Status Tracking)" />
          </Tabs>
        </Box>

        {/* SHEETS TAB */}
        <TabPanel value={tab} index={0}>
          <Typography variant="body2" color="text.secondary" paragraph>
            Google Sheets is the source of truth for new job intakes. Select rows to officially import them into the Unscheduled Pool.
          </Typography>

          {loadingSheets ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox 
                        checked={sheetData.length > 0 && selectedSheets.length === sheetData.filter(d => !d.is_imported).length}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedSheets(sheetData.filter(d => !d.is_imported).map(d => d.entry_id));
                          } else {
                            setSelectedSheets([]);
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Client</TableCell>
                    <TableCell>Address</TableCell>
                    <TableCell>Purpose</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sheetData.map((row) => (
                    <TableRow key={row.entry_id} selected={selectedSheets.includes(row.entry_id)} hover>
                      <TableCell padding="checkbox">
                        <Checkbox 
                          checked={selectedSheets.includes(row.entry_id)}
                          onChange={() => toggleSheetSelect(row.entry_id)}
                          disabled={row.is_imported}
                        />
                      </TableCell>
                      <TableCell>
                        {row.is_imported ? (
                          <Chip label="Imported" size="small" color="default" />
                        ) : (
                          <Chip label="New" size="small" color="success" />
                        )}
                      </TableCell>
                      <TableCell>{row.client_name}</TableCell>
                      <TableCell>{row.address}</TableCell>
                      <TableCell>{row.purpose}</TableCell>
                    </TableRow>
                  ))}
                  {sheetData.length === 0 && (
                    <TableRow><TableCell colSpan={5} align="center">No recent entries found in Sheet.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button 
              variant="contained" 
              onClick={handleImportSheets} 
              disabled={selectedSheets.length === 0 || importingSheets}
              startIcon={importingSheets ? <CircularProgress size={20} /> : <SyncIcon />}
            >
              Import {selectedSheets.length} Selected Jobs
            </Button>
          </Box>
        </TabPanel>

        {/* CALENDAR TAB */}
        <TabPanel value={tab} index={1}>
          <Typography variant="body2" color="text.secondary" paragraph>
            Google Calendar tracks scheduled items. Use this to find jobs that were done in the real world and update their status in the app.
          </Typography>

          {loadingCalendar ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox 
                        checked={calendarData.length > 0 && selectedCalendar.length === calendarData.length}
                        onChange={(e) => {
                          if (e.target.checked) setSelectedCalendar(calendarData.map(d => d.event_id));
                          else setSelectedCalendar([]);
                        }}
                      />
                    </TableCell>
                    <TableCell>Time</TableCell>
                    <TableCell>Event Summary</TableCell>
                    <TableCell>Mapped Job Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {calendarData.map((row) => (
                    <TableRow key={row.event_id} selected={selectedCalendar.includes(row.event_id)} hover>
                      <TableCell padding="checkbox">
                        <Checkbox 
                          checked={selectedCalendar.includes(row.event_id)}
                          onChange={() => toggleCalendarSelect(row.event_id)}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                          {parseUtc(row.start).toLocaleDateString()} {row.is_past && <Chip label="Past" size="small" sx={{ height: 16, fontSize: '0.6rem', ml: 1 }} />}
                        </Typography>
                      </TableCell>
                      <TableCell>{row.summary}</TableCell>
                      <TableCell>
                        {row.mapped_job_id ? (
                          <Chip 
                            label={row.mapped_job_status || 'Unknown'} 
                            size="small" 
                            color={row.mapped_job_status === 'completed' ? 'success' : row.mapped_job_status === 'scheduled' ? 'info' : 'warning'} 
                          />
                        ) : (
                          <Typography variant="caption" color="text.secondary">No matching job found in DB</Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {calendarData.length === 0 && (
                    <TableRow><TableCell colSpan={4} align="center">No recent calendar events found.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <Box sx={{ mt: 2, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button 
              variant="outlined" 
              color="info"
              onClick={() => handleUpdateCalendarStatus('scheduled')} 
              disabled={selectedCalendar.length === 0 || updatingCalendar}
            >
              Mark Selected as Scheduled
            </Button>
            <Button 
              variant="contained" 
              color="success"
              onClick={() => handleUpdateCalendarStatus('completed')} 
              disabled={selectedCalendar.length === 0 || updatingCalendar}
            >
              Mark Selected as Completed
            </Button>
          </Box>
        </TabPanel>
      </DialogContent>
    </Dialog>
  );
};

export default SyncModal;